/**
 * Stammplatz, Vollständigkeit und „Alles da“ auf dem Gerät (ADR 0013, ADR 0015).
 *
 * Die Regeln stehen in `@thw/core-domain` (`stammplatz.ts`). Hier werden sie
 * auf die lokale Kopie angewandt: Vollständigkeit und „Alles da“ rechnen
 * offline, die Buchungen gehen durch die Warteschlange.
 *
 * **Den Stammplatz setzen** ist die Ausnahme. Er ist ein Stammdatum und geht
 * nur mit Netz (ADR 0006) -- ohne Umweg über die Warteschlange, damit keiner
 * glaubt, er sei gesetzt, während er noch irgendwo wartet.
 */

import type { Artikel, StammplatzGesetzt } from "@thw/contracts";
import {
  allesDaBewegungen,
  allesDaPlan,
  vollstaendigkeit,
  type AllesDaPlan,
  type Vollstaendigkeit,
  type Welt,
} from "@thw/core-domain";
import { anfrage } from "../api/klient.js";
import { artikelUebernehmen } from "./abgleich.js";
import type { Bestand } from "./bestand.js";
import { bewegungBuchen, neuerVorgang, type Gebucht } from "./verschieben.js";

const welten = new WeakMap<Bestand, Welt>();

/** Die Welt der Regeln aus der lokalen Kopie -- je Stand des Bestands nur einmal gebaut. */
export function weltAus(bestand: Bestand): Welt {
  const vorhanden = welten.get(bestand);
  if (vorhanden !== undefined) return vorhanden;
  const stammplaetze = new Map<string, string | null>();
  for (const a of bestand.artikel.values()) {
    // Eine lokale Kopie von vor Vertrag 2.6 kennt das Feld nicht (undefined).
    stammplaetze.set(a.id, a.stammplatz_id ?? null);
  }
  const welt: Welt = {
    orte: bestand.lagerorte,
    stammplaetze,
    zustaende: bestand.zustaende,
    // Eine lokale Kopie von vor Vertrag 2.10 kennt den Haken nicht (ADR 0025).
    rueckbuchungIgnorieren: new Set(
      bestand.statusArten
        .filter((a) => (a.rueckbuchung_ignorieren as boolean | undefined) === true)
        .map((a) => a.schluessel),
    ),
  };
  welten.set(bestand, welt);
  return welt;
}

/** Der Stammplatz eines Artikels, `null` ohne. */
export function stammplatzVon(bestand: Bestand, artikel_id: string): string | null {
  return bestand.artikel.get(artikel_id)?.stammplatz_id ?? null;
}

export function vollstaendigkeitVon(bestand: Bestand, lagerort_id: string): Vollstaendigkeit {
  return vollstaendigkeit(lagerort_id, weltAus(bestand));
}

export function allesDaFuer(bestand: Bestand, lagerort_id: string): AllesDaPlan {
  return allesDaPlan(lagerort_id, weltAus(bestand));
}

/** „vollständig“, „1 fehlt“, „3 fehlen“ -- für Kopf und Zeilen. */
export function fehltText(anzahl: number): string {
  if (anzahl === 0) return "vollständig";
  return anzahl === 1 ? "1 fehlt" : `${String(anzahl)} fehlen`;
}

/**
 * „Alles da“ buchen: die gewählten Zeilen, in ihrer Reihenfolge, als ein
 * Vorgang. `bestand` sollte frisch sein -- der Plan wird damit neu gerechnet,
 * falls sich seit dem Öffnen der Übersicht etwas bewegt hat.
 */
export async function allesDaBuchen(
  bestand: Bestand,
  lagerort_id: string,
  gewaehlt: ReadonlySet<string>,
): Promise<{ gebucht: Gebucht[]; verboten: string[] }> {
  const welt = weltAus(bestand);
  const { buchungen, verboten } = allesDaBewegungen(allesDaPlan(lagerort_id, welt), gewaehlt, welt);
  const vorgang = neuerVorgang();
  const gebucht: Gebucht[] = [];
  for (const b of buchungen) {
    gebucht.push(await bewegungBuchen(bestand, b.artikel_id, b.bewegung, vorgang));
    vorgang.stelle += 1;
  }
  return { gebucht, verboten };
}

/**
 * Den Stammplatz setzen oder entfernen -- für einen Artikel oder eine ganze
 * Sammelliste. Nur mit Netz; ohne wirft es, und nichts ist gesetzt.
 */
export async function stammplatzSetzen(
  artikel_ids: readonly string[],
  stammplatz_id: string | null,
): Promise<Artikel[]> {
  if (!globalThis.navigator.onLine) {
    throw new Error("Den Stammplatz ändern geht nur mit Netz.");
  }
  let antwort: StammplatzGesetzt;
  try {
    antwort = await anfrage<StammplatzGesetzt>(
      "PUT",
      "/api/core/stammplatz",
      { artikel_ids, stammplatz_id },
      15_000,
    );
  } catch (fehler) {
    // `fetch` ohne Verbindung und das Zeitlimit: Das Handy hält sich für
    // online, der Server ist aber nicht zu erreichen.
    if (fehler instanceof TypeError || fehler instanceof DOMException) {
      throw new Error("Keine Verbindung zum Server — der Stammplatz ist nicht gesetzt.", {
        cause: fehler,
      });
    }
    throw fehler;
  }
  await artikelUebernehmen(antwort.artikel);
  return antwort.artikel;
}
